// ProtectedRoute — FE-A.
//
// Redirects to /login when the auth context reports unauthenticated.
// Renders the matched child route otherwise. The loading branch is
// a tiny inline spinner; FE-051 may replace it with a branded shell.

import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "./AuthContext";
import {
  currentShellFromLocation,
  parseReturnPathFromState,
  shellHome,
  type AppShell,
} from "@/lib/navigation";


export function ProtectedRoute({ shell }: { shell?: AppShell }) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.status === "loading") {
    return (
      <div
        data-testid="auth-loading"
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center bg-canvas text-sm text-text-secondary"
      >
        <span className="sr-only">Checking your session…</span>
        <span aria-hidden="true">Checking session…</span>
      </div>
    );
  }
  if (auth.status === "unauthenticated") {
    const activeShell = shell ?? currentShellFromLocation();
    // Preserve path + query + hash so an unauthenticated deep link to any
    // surfaced route restores to its exact location after login (FE-SP-002B).
    const from = parseReturnPathFromState(
      location.state ?? `${location.pathname}${location.search}${location.hash}`,
      shellHome(activeShell),
      activeShell,
    );
    return (
      <Navigate
        to="/login"
        replace
        state={{ from }}
      />
    );
  }
  if (auth.status === "error") {
    return (
      <div
        data-testid="auth-error"
        role="alert"
        className="flex min-h-screen items-center justify-center bg-canvas px-6 text-center text-sm text-text-primary"
      >
        Something went wrong checking your session. Refresh to try again.
      </div>
    );
  }
  return <Outlet />;
}
